import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';
import { useCanvasStore } from '@/modules/canvas/store';
import {
  createVehicleGeneratedLayerName,
  GeneratedLayerNames,
  getGeneratedLayerName,
  Layer,
  LayerName,
  LayerNames,
  Layers,
  LayerVisibilityMode,
  StoredLayer,
  StoredLayers,
  VehicleGeneratedLayerName,
  isLayerName,
  isLayerNames,
} from '@/modules/common/types/layers';
import { UserPreferenceName, getUserPreference } from '@/modules/userPreferences';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { getPoseIdsFromPois } from '@/modules/floorplanDesign/mappers/helpers/helpers';
import { useAppStore } from '@/modules/app/store/useAppStore';

import { getChildLayers, getParentLayer, getRelativeLayers } from '../layerHierarchy';
import { getLayerGroupOfLayerType, getLayerOfLayerType, sortObjects } from '../helpers';
import { getLayerDefinition, getLayerVisibilityModePreference } from '../layerTree';
import { getEffectiveLayerVisibility } from '../visibility';

type LayerState = {
  layers: Layers;
  invisibleElements: Set<string>;
  lockedElements: Set<string>;
};

type LayerActions = {
  layerContainsSelectedShapes(layer: LayerNames): boolean;
  loadLayersState(layers: StoredLayers): void;
  setLayerVisibilityMode(layerName: LayerNames, mode: LayerVisibilityMode): void;
  setVehicleGeneratedLayerVisibilityMode(
    layerName: VehicleGeneratedLayerName,
    mode: LayerVisibilityMode,
  ): void;
  refreshVehicleGeneratedLayerVisibility(): void;
  loadLayerVisibilityModes(visibilityModes: Partial<Record<LayerNames, LayerVisibilityMode>>): void;
};

type VisibilityActions = {
  showAllElements(): void;
  hideElements(idsToHide: string[]): void;
  showElements(idsToShow: string[]): void;
  isElementVisible(id: string): boolean;
  isLayerVisible(layerName: LayerNames): boolean;
  isEverythingVisible(): boolean;
};

type LockActions = {
  isLayerLocked(layerName: LayerNames): boolean;
  setLayerLock(layerName: LayerNames, lock: boolean): void;
  lockElements(idsToLock: string[]): void;
  unlockElements(idsToUnlock: string[]): void;
  isElementLocked(id: string): boolean;
};

const DEFAULT_LAYER_VISIBILITY_MODE: LayerVisibilityMode = 'auto';

const isLayerVisibilityMode = (value: unknown): value is LayerVisibilityMode =>
  value === 'auto' || value === 'force_show' || value === 'force_hide';

const getDefinitionLayerName = (layerName: LayerName): LayerNames | undefined => {
  if (isLayerNames(layerName)) return layerName;

  return getGeneratedLayerName(layerName);
};

const getDefaultVisibleLayerMode = (layerName: LayerName): LayerVisibilityMode => {
  const definitionLayerName = getDefinitionLayerName(layerName);
  const definition = definitionLayerName ? getLayerDefinition(definitionLayerName) : undefined;

  if (definition?.visibilityMode) return definition.visibilityMode;
  if (definition?.lod) return DEFAULT_LAYER_VISIBILITY_MODE;

  return 'force_show';
};

const normalizeVisibilityMode = (
  layerName: LayerName,
  mode: LayerVisibilityMode,
): LayerVisibilityMode => {
  if (mode !== 'auto') return mode;

  const definitionLayerName = getDefinitionLayerName(layerName);
  const definition = definitionLayerName ? getLayerDefinition(definitionLayerName) : undefined;
  if (definition?.lod) return 'auto';

  return 'force_show';
};

const getInitialVisibilityMode = (layerName: LayerName): LayerVisibilityMode => {
  const preference = isLayerNames(layerName)
    ? getLayerVisibilityModePreference(layerName)
    : undefined;
  const preferenceValue = preference ? getUserPreference(preference) : undefined;

  if (isLayerVisibilityMode(preferenceValue)) {
    return normalizeVisibilityMode(layerName, preferenceValue);
  }

  return getDefaultVisibleLayerMode(layerName);
};

const getLayerVisibilityTargets = (layerName: LayerNames): LayerNames[] => {
  const childLayers = getChildLayers(layerName);

  if (childLayers.length > 0) {
    return [...new Set(childLayers)];
  }

  return [layerName];
};

const createInitialLayer = (layerName: LayerName): Layer => {
  const visibilityMode = getInitialVisibilityMode(layerName);

  return {
    locked: false,
    visibilityMode,
  };
};

const getVehicleGeneratedLayerElementIds = (layerName: VehicleGeneratedLayerName): string[] => {
  const generatedLayerName = getGeneratedLayerName(layerName);
  if (!generatedLayerName) return [];

  const vehicleLayout = useFloorplanDesignStore
    .getState()
    .floorplan.vehicleLayouts.find(
      (layout) =>
        createVehicleGeneratedLayerName(layout.vehicleTypeId, generatedLayerName) === layerName,
    );

  if (!vehicleLayout) return [];

  if (generatedLayerName === GeneratedLayerNames.POIS) {
    return vehicleLayout.pois.flatMap((poi) => [
      poi.id,
      `${poi.id}-highway-lane`,
      `${poi.id}-pose`,
    ]);
  }

  if (generatedLayerName === GeneratedLayerNames.POI_CONNECTIONS) {
    return vehicleLayout.poiConnections.map((connection) => connection.id);
  }

  if (generatedLayerName === GeneratedLayerNames.TRACKS) {
    return [`track-batch-${vehicleLayout.vehicleTypeId}`];
  }

  if (generatedLayerName === GeneratedLayerNames.SWEPT_PATHS) {
    return [`track-swept-path-batch-${vehicleLayout.vehicleTypeId}`];
  }

  return [];
};

const getVehicleGeneratedLayerNames = (): VehicleGeneratedLayerName[] =>
  useFloorplanDesignStore
    .getState()
    .floorplan.vehicleLayouts.flatMap((vehicleLayout) =>
      Object.values(GeneratedLayerNames).map((generatedLayerName) =>
        createVehicleGeneratedLayerName(vehicleLayout.vehicleTypeId, generatedLayerName),
      ),
    );

const getNativeZoomPreferenceValue = (preference?: UserPreferenceName): number | undefined => {
  if (!preference) return undefined;

  const preferenceValue = getUserPreference(preference);
  if (typeof preferenceValue !== 'number') return undefined;

  const { nativeZoomToUserZoomFactor } = useCanvasStore.getState();
  if (nativeZoomToUserZoomFactor <= 0) return preferenceValue;

  return preferenceValue / nativeZoomToUserZoomFactor;
};

const getNativeHideFromZoomPreferenceValue = (
  preference?: UserPreferenceName,
): number | undefined => {
  if (preference !== UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD) {
    return getNativeZoomPreferenceValue(preference);
  }

  if (useFloorplanDesignStore.getState().tracks?.length) {
    return getNativeZoomPreferenceValue(preference);
  }

  return undefined;
};

const getVehicleGeneratedLayerVisibility = (
  layerName: VehicleGeneratedLayerName,
  layers: Layers,
  zoom: number,
): boolean => {
  const generatedLayerName = getGeneratedLayerName(layerName);
  if (!generatedLayerName) return true;

  const definition = getLayerDefinition(generatedLayerName);
  const visibilityMode =
    layers[layerName]?.visibilityMode ??
    layers[generatedLayerName]?.visibilityMode ??
    getInitialVisibilityMode(generatedLayerName);
  const showFromZoomPreference = definition?.lod?.showFromZoomPreference;
  const hideFromZoomPreference = definition?.lod?.hideFromZoomPreference;
  const showFromZoom = getNativeZoomPreferenceValue(showFromZoomPreference);
  const hideFromZoom = getNativeHideFromZoomPreferenceValue(hideFromZoomPreference);

  return getEffectiveLayerVisibility({
    visibilityMode,
    parentVisible: true,
    zoom,
    showFromZoom,
    hideFromZoom,
  });
};

const getStaticGeneratedLayerVisibility = (
  layerName: LayerNames,
  layers: Layers,
  zoom: number,
): boolean => {
  const definition = getLayerDefinition(layerName);
  const visibilityMode = layers[layerName]?.visibilityMode ?? getInitialVisibilityMode(layerName);
  const showFromZoom = getNativeZoomPreferenceValue(definition?.lod?.showFromZoomPreference);
  const hideFromZoom = getNativeHideFromZoomPreferenceValue(
    definition?.lod?.hideFromZoomPreference,
  );

  return getEffectiveLayerVisibility({
    visibilityMode,
    parentVisible: true,
    zoom,
    showFromZoom,
    hideFromZoom,
  });
};

const setVehicleGeneratedLayerElementVisibility = (
  layerName: VehicleGeneratedLayerName,
  layers: Layers,
): void => {
  const canvas = useCanvasStore.getState().instance;
  if (!canvas) return;

  const zoom = canvas.getZoom();
  const visible = getVehicleGeneratedLayerVisibility(layerName, layers, zoom);
  let elementIds = [...new Set(getVehicleGeneratedLayerElementIds(layerName))];
  if (elementIds.length === 0) return;

  if (!visible) {
    const generatedLayerName = getGeneratedLayerName(layerName);

    if (generatedLayerName) {
      const visibleElementIdsFromOtherVehicleTypes = new Set(
        useFloorplanDesignStore.getState().floorplan.vehicleLayouts.flatMap((vehicleLayout) => {
          const otherLayerName = createVehicleGeneratedLayerName(
            vehicleLayout.vehicleTypeId,
            generatedLayerName,
          );
          if (otherLayerName === layerName) return [];

          if (!getVehicleGeneratedLayerVisibility(otherLayerName, layers, zoom)) return [];

          return getVehicleGeneratedLayerElementIds(otherLayerName);
        }),
      );

      elementIds = elementIds.filter(
        (elementId) => !visibleElementIdsFromOtherVehicleTypes.has(elementId),
      );
    }
  }

  elementIds = elementIds.filter((elementId) => Boolean(canvas.getElement(elementId)));
  if (elementIds.length === 0) return;

  canvas.setElementVisibility(elementIds, visible, true);
};

const setStaticGeneratedStationVisibility = (layers: Layers): void => {
  const canvas = useCanvasStore.getState().instance;
  if (!canvas) return;

  const stationIds = useFloorplanDesignStore
    .getState()
    .floorplan.stations.map((station) => station.id)
    .filter((stationId) => Boolean(canvas.getElement(stationId)));
  if (stationIds.length === 0) return;

  const visible = getStaticGeneratedLayerVisibility(
    LayerNames.GENERATED_STATIONS,
    layers,
    canvas.getZoom(),
  );

  canvas.setElementVisibility(stationIds, visible, true);
};

const refreshGeneratedLayerVisibility = (layers: Layers): void => {
  setStaticGeneratedStationVisibility(layers);

  getVehicleGeneratedLayerNames().forEach((layerName) => {
    setVehicleGeneratedLayerElementVisibility(layerName, layers);
  });
};

const createInitialLayers = (): Layers => ({
  [LayerNames.GENERATED_POIS]: createInitialLayer(LayerNames.GENERATED_POIS),
  [LayerNames.GENERATED_STATIONS]: createInitialLayer(LayerNames.GENERATED_STATIONS),
  [LayerNames.TRACKS]: createInitialLayer(LayerNames.TRACKS),
  [LayerNames.SWEPT_PATHS]: createInitialLayer(LayerNames.SWEPT_PATHS),
  [LayerNames.CONNECTIONS]: createInitialLayer(LayerNames.CONNECTIONS),
  [LayerNames.CONNECTION_BUBBLES]: createInitialLayer(LayerNames.CONNECTION_BUBBLES),
  [LayerNames.VEHICLE_LAYOUT_POI_CONNECTIONS]: createInitialLayer(
    LayerNames.VEHICLE_LAYOUT_POI_CONNECTIONS,
  ),
  [LayerNames.ROADS]: createInitialLayer(LayerNames.ROADS),
  [LayerNames.HIGHWAYS]: createInitialLayer(LayerNames.HIGHWAYS),
  [LayerNames.ANGLED_HIGHWAYS]: createInitialLayer(LayerNames.ANGLED_HIGHWAYS),
  [LayerNames.AREAS]: createInitialLayer(LayerNames.AREAS),
  [LayerNames.INTAKE]: createInitialLayer(LayerNames.INTAKE),
  [LayerNames.DELIVERY]: createInitialLayer(LayerNames.DELIVERY),
  [LayerNames.STORAGE]: createInitialLayer(LayerNames.STORAGE),
  [LayerNames.CHARGING]: createInitialLayer(LayerNames.CHARGING),
  [LayerNames.PARKING]: createInitialLayer(LayerNames.PARKING),
  [LayerNames.MANUAL]: createInitialLayer(LayerNames.MANUAL),
  [LayerNames.EXTRACTION]: createInitialLayer(LayerNames.EXTRACTION),
  [LayerNames.INSERTION]: createInitialLayer(LayerNames.INSERTION),
  [LayerNames.PROCESS]: createInitialLayer(LayerNames.PROCESS),
  [LayerNames.INTERIOR]: createInitialLayer(LayerNames.INTERIOR),
  [LayerNames.WALLS]: createInitialLayer(LayerNames.WALLS),
  [LayerNames.OBSTACLES]: createInitialLayer(LayerNames.OBSTACLES),
  [LayerNames.REFERENCE]: createInitialLayer(LayerNames.REFERENCE),
  [LayerNames.LOGICAL_SPACES]: createInitialLayer(LayerNames.LOGICAL_SPACES),
  [LayerNames.GROUPS]: createInitialLayer(LayerNames.GROUPS),
  [LayerNames.FLOWS]: createInitialLayer(LayerNames.FLOWS),
  [LayerNames.FLOORPLAN]: createInitialLayer(LayerNames.FLOORPLAN),
  [LayerNames.NAMES]: createInitialLayer(LayerNames.NAMES),
  [LayerNames.AREA_NAMES]: createInitialLayer(LayerNames.AREA_NAMES),
  [LayerNames.HIGHWAY_NAMES]: createInitialLayer(LayerNames.HIGHWAY_NAMES),
  [LayerNames.OBSTACLE_NAMES]: createInitialLayer(LayerNames.OBSTACLE_NAMES),
  [LayerNames.ORIGIN]: createInitialLayer(LayerNames.ORIGIN),
});

const INITIAL_STATE: LayerState = {
  layers: createInitialLayers(),
  invisibleElements: new Set<string>(),
  lockedElements: new Set<string>(),
};

const getStoredLayer = (layers: StoredLayers, layerName: LayerName): StoredLayer | undefined =>
  layers[layerName];

const getStoredLayerVisibilityMode = (
  layerName: LayerName,
  storedLayer: StoredLayer | undefined,
): LayerVisibilityMode | undefined => {
  if (!storedLayer?.visibilityMode) return undefined;

  return normalizeVisibilityMode(layerName, storedLayer.visibilityMode);
};

const normalizeLayersState = (layers: StoredLayers): Layers => {
  const nextLayers: Layers = createInitialLayers();

  Object.keys(layers)
    .filter(isLayerName)
    .forEach((layerName) => {
      const storedLayer = getStoredLayer(layers, layerName);
      if (!storedLayer) return;

      const visibilityMode =
        getStoredLayerVisibilityMode(layerName, storedLayer) ??
        nextLayers[layerName]?.visibilityMode ??
        getInitialVisibilityMode(layerName);

      nextLayers[layerName] = {
        ...(nextLayers[layerName] ?? createInitialLayer(layerName)),
        ...storedLayer,
        visibilityMode,
      };
    });

  return nextLayers;
};

const getLayersWithForceHiddenLayersReset = (layers: Layers): Layers =>
  Object.keys(layers)
    .filter(isLayerName)
    .reduce<Layers>(
      (nextLayers, layerName) => {
        const currentLayer = layers[layerName] ?? createInitialLayer(layerName);

        if (currentLayer.visibilityMode !== 'force_hide') {
          nextLayers[layerName] = currentLayer;
          return nextLayers;
        }

        const visibilityMode = getDefaultVisibleLayerMode(layerName);

        nextLayers[layerName] = {
          ...currentLayer,
          visibilityMode,
        };

        return nextLayers;
      },
      { ...layers },
    );

/**
 * For the given ids, find any that are areas in the floorplan design and return
 * the ids of their associated stations, pois, and poses so those
 * can be hidden/shown alongside the area on the canvas.
 *
 * Only elements that are NOT parented to the area are returned here, because
 * `canvas.setElementVisibility` is called recursively and will handle parented
 * children automatically (e.g. pose background/needle children of the pose
 * group).
 */
const collectAreaDescendantIds = (ids: string[]): string[] => {
  const { getAreasByIds, getPoisByAreaIds, getStationsByAreaIds } =
    useFloorplanDesignStore.getState();

  const flatAreas = areasToFlatCollection(getAreasByIds(ids));
  if (flatAreas.length === 0) return [];

  const areaAndSegmentIds = flatAreas.flatMap((area) =>
    isHighwayArea(area) ? area.segments.map((segment) => segment.id) : area.id,
  );

  const stationIds = getStationsByAreaIds(ids).map((station) => station.id);
  const poiIds = getPoisByAreaIds(areaAndSegmentIds).map((poi) => poi.id);
  const poseIds = getPoseIdsFromPois(poiIds);

  return [...stationIds, ...poiIds, ...poseIds];
};

export const useLayerStore = create<LayerState & LayerActions & VisibilityActions & LockActions>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      // #region Layer actions

      layerContainsSelectedShapes(layer: LayerNames) {
        const shapeStates = sortObjects(
          useFloorplanDesignStore.getState().design,
          useFloorplanDesignStore.getState().uxonly,
        );
        const matchingShapes = shapeStates.filter((shape) =>
          useAppStore.getState().selectedIds.includes(shape.id),
        );

        const containsSelectedShapes =
          matchingShapes.some((shape) => layer === getLayerOfLayerType(shape.type)) ||
          matchingShapes.some((shape) => layer === getLayerGroupOfLayerType(shape.type));

        return containsSelectedShapes;
      },

      setLayerVisibilityMode(layerName, mode) {
        const targetLayerNames = getLayerVisibilityTargets(layerName);
        const targetVisibilityModes = targetLayerNames.map((targetLayerName) => ({
          layerName: targetLayerName,
          visibilityMode: normalizeVisibilityMode(targetLayerName, mode),
        }));

        set(
          (state) => {
            const nextLayers = { ...state.layers };

            targetVisibilityModes.forEach(({ layerName: targetLayerName, visibilityMode }) => {
              nextLayers[targetLayerName] = {
                ...(nextLayers[targetLayerName] ?? createInitialLayer(targetLayerName)),
                visibilityMode,
              };
            });

            return {
              layers: nextLayers,
            };
          },
          undefined,
          {
            type: 'setLayerVisibilityMode',
          },
        );

        refreshGeneratedLayerVisibility(get().layers);
      },

      setVehicleGeneratedLayerVisibilityMode(layerName, mode) {
        const visibilityMode = normalizeVisibilityMode(layerName, mode);

        set(
          (state) => ({
            layers: {
              ...state.layers,
              [layerName]: {
                ...(state.layers[layerName] ?? createInitialLayer(layerName)),
                visibilityMode,
              },
            },
          }),
          undefined,
          {
            type: 'setVehicleGeneratedLayerVisibilityMode',
          },
        );

        setVehicleGeneratedLayerElementVisibility(layerName, get().layers);
      },

      refreshVehicleGeneratedLayerVisibility() {
        refreshGeneratedLayerVisibility(get().layers);
      },

      // #endregion

      // #region Visibility actions

      isLayerVisible(layerName) {
        return get().layers[layerName].visibilityMode !== 'force_hide';
      },

      isElementVisible(id) {
        return !get().invisibleElements.has(id);
      },

      showAllElements() {
        const canvas = useCanvasStore.getState()?.instance;
        if (!canvas) return;

        const previouslyHidden = [...get().invisibleElements];
        const cascadeIds = collectAreaDescendantIds(previouslyHidden);
        canvas.setElementVisibility([...previouslyHidden, ...cascadeIds], true, true);

        const layers = getLayersWithForceHiddenLayersReset(get().layers);
        refreshGeneratedLayerVisibility(layers);

        set(
          () => ({
            layers,
            invisibleElements: new Set<string>(),
          }),
          undefined,
          {
            type: 'showAllElements',
          },
        );
      },

      hideElements(idsToHide) {
        const canvas = useCanvasStore.getState()?.instance;
        if (!canvas) return;

        const cascadeIds = collectAreaDescendantIds(idsToHide);
        canvas.setElementVisibility([...idsToHide, ...cascadeIds], false, true);

        const newInvisibleElements = new Set(get().invisibleElements);
        idsToHide.forEach((id) => newInvisibleElements.add(id));

        set(
          () => ({
            invisibleElements: newInvisibleElements,
          }),
          undefined,
          {
            type: 'hideElements',
          },
        );
      },

      showElements(idsToShow: string[]) {
        const canvas = useCanvasStore.getState()?.instance;
        if (!canvas) return;

        const cascadeIds = collectAreaDescendantIds(idsToShow);
        canvas.setElementVisibility([...idsToShow, ...cascadeIds], true, true);
        refreshGeneratedLayerVisibility(get().layers);

        const newInvisibleElements = new Set(get().invisibleElements);
        idsToShow.forEach((id) => newInvisibleElements.delete(id));

        set(
          () => ({
            invisibleElements: newInvisibleElements,
          }),
          undefined,
          {
            type: 'showElements',
          },
        );
      },

      isEverythingVisible() {
        if (get().invisibleElements.size > 0) return false;

        const layerNames = Object.keys(get().layers).filter(isLayerName);

        return layerNames.every((layerName) => {
          const visibilityMode =
            get().layers[layerName]?.visibilityMode ?? getInitialVisibilityMode(layerName);

          return visibilityMode !== 'force_hide';
        });
      },

      // #endregion

      // #region Lock actions

      isLayerLocked(layerName) {
        return get().layers[layerName].locked;
      },

      setLayerLock(layerName, lock) {
        const canvas = useCanvasStore.getState()?.instance;
        if (!canvas) return;

        const shapesToUnlock: string[] = [];
        const shapesToLock: string[] = [];
        const parentLayer = getParentLayer(layerName);
        const layerRelatives = getRelativeLayers(layerName);
        const shapeStates = sortObjects(
          useFloorplanDesignStore.getState().design,
          useFloorplanDesignStore.getState().uxonly,
        );

        layerRelatives.forEach((layer) => {
          shapeStates.forEach((shape) => {
            const layerFromType = getLayerOfLayerType(shape.type);
            if (layerFromType === layer) {
              if (lock) {
                shapesToLock.push(shape.id);
              } else if (!get().isLayerLocked(layerFromType)) {
                shapesToUnlock.push(shape.id);
              }
            }
          });
        });

        if (lock) {
          shapeStates.forEach((item) => {
            const layerNameFromShapeType = getLayerOfLayerType(item.type);
            if (layerName === layerNameFromShapeType) {
              shapesToLock.push(item.id);
            }
          });
        } else {
          shapeStates.forEach((item) => {
            const layerNameFromShapeType = getLayerOfLayerType(item.type);
            if (layerName === layerNameFromShapeType) {
              shapesToUnlock.push(item.id);
            }
          });
        }

        if (lock) {
          canvas.updateInteractivity(shapesToLock, {
            selectable: false,
          });
        } else if (!parentLayer || !get().isLayerLocked(parentLayer)) {
          canvas.updateInteractivity(
            shapesToUnlock.filter((element) => !get().lockedElements.has(element)),
            {
              selectable: true,
            },
          );
        }

        set(
          (state) => ({
            layers: {
              ...state.layers,
              [layerName]: {
                ...(state.layers[layerName] ?? createInitialLayer(layerName)),
                locked: lock,
              },
            },
          }),
          undefined,
          {
            type: 'setLayerLock',
          },
        );
      },

      lockElements(idsToLock: string[]) {
        const canvas = useCanvasStore.getState()?.instance;
        if (!canvas) return;

        canvas.updateInteractivity(idsToLock, {
          selectable: false,
        });

        const newLockedElements = new Set(get().lockedElements);
        idsToLock.forEach((id) => newLockedElements.add(id));

        set(
          () => ({
            lockedElements: newLockedElements,
          }),
          undefined,
          {
            type: 'lockElements',
          },
        );
      },

      unlockElements(idsToUnlock: string[]) {
        const canvas = useCanvasStore.getState()?.instance;
        if (!canvas) return;

        const shapeStates = sortObjects(
          useFloorplanDesignStore.getState().design,
          useFloorplanDesignStore.getState().uxonly,
        );
        const matchingShapes = shapeStates
          .filter((shape) => idsToUnlock.includes(shape.id))
          .filter((shape) => {
            const layer = getLayerOfLayerType(shape.type);
            if (!layer) return false;

            return !get().isLayerLocked(layer);
          })
          .map((shape) => shape.id);

        canvas.updateInteractivity(matchingShapes, {
          selectable: true,
        });

        const newLockedElements = new Set(get().lockedElements);
        idsToUnlock.forEach((id) => newLockedElements.delete(id));

        set(
          () => ({
            lockedElements: newLockedElements,
          }),
          undefined,
          {
            type: 'unlockElements',
          },
        );
      },

      isElementLocked(id) {
        return get().lockedElements.has(id);
      },

      // #endregion

      // #region Persistence
      loadLayerVisibilityModes(visibilityModes) {
        set(
          (state) => {
            const nextLayers = { ...state.layers };

            Object.entries(visibilityModes).forEach(([layerName, mode]) => {
              if (!isLayerNames(layerName)) return;
              if (!isLayerVisibilityMode(mode)) return;

              const visibilityMode = normalizeVisibilityMode(layerName, mode);

              nextLayers[layerName] = {
                ...(nextLayers[layerName] ?? createInitialLayer(layerName)),
                visibilityMode,
              };
            });

            return {
              layers: nextLayers,
            };
          },
          undefined,
          {
            type: 'loadLayerVisibilityModes',
          },
        );
      },

      loadLayersState(layers) {
        set(
          () => ({
            layers: normalizeLayersState(layers),
          }),
          undefined,
          {
            type: 'loadLayersState',
          },
        );
      },

      // #endregion
    }),
    { store: 'layerStore', ...DEVTOOLS_OPTIONS },
  ),
);
